import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  makeStyles,
  Switch,
  FormControlLabel,
  Typography,
  Box,
} from '@material-ui/core';
import '@mdxeditor/editor/style.css';

import {
  toolbarPlugin,
  listsPlugin,
  quotePlugin,
  headingsPlugin,
  linkPlugin,
  linkDialogPlugin,
  tablePlugin,
  thematicBreakPlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  directivesPlugin,
  AdmonitionDirectiveDescriptor,
  diffSourcePlugin,
  markdownShortcutPlugin,
  MDXEditor,
  BoldItalicUnderlineToggles,
  ListsToggle,
  CreateLink,
  InsertTable,
  DiffSourceToggleWrapper,
  UndoRedo,
  Separator,
  CodeToggle,
  StrikeThroughSupSubToggles,
  InsertThematicBreak,
  InsertCodeBlock,
  imagePlugin,
  ImageUploadHandler,
} from '@mdxeditor/editor';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { convertMarkdownToPhabricatorRemarkup, convertToBase64, removeBase64Prefix } from './utils';
import { useSearchFile, useUploadFile } from './hooks';

const toolbarContents = () => (
  <DiffSourceToggleWrapper>
    <UndoRedo />
    <Separator />
    <BoldItalicUnderlineToggles />
    <CodeToggle />
    <Separator />
    <StrikeThroughSupSubToggles />
    <Separator />
    <ListsToggle />
    <Separator />

    <CreateLink />
    <Separator />

    <InsertTable />
    <InsertThematicBreak />

    <Separator />
    <InsertCodeBlock />

    <Separator />
  </DiffSourceToggleWrapper>
);

const allPlugins = (imageUploadHandler: ImageUploadHandler) => [
  toolbarPlugin({
    toolbarContents,
  }),
  listsPlugin(),
  quotePlugin(),
  headingsPlugin(),
  linkPlugin(),
  linkDialogPlugin(),
  tablePlugin(),
  thematicBreakPlugin(),
  imagePlugin({
    imageUploadHandler,
    disableImageResize: true,
  }),
  codeBlockPlugin({ defaultCodeBlockLanguage: 'txt' }),
  codeMirrorPlugin({
    codeBlockLanguages: {
      txt: 'Text',
      cs: 'C#',
      css: 'CSS',
      go: 'Go',
      html: 'HTML',
      js: 'JavaScript',
      jsx: 'JavaScript (React)',
      json: 'JSON',
      php: 'PHP',
      py: 'Python',
      sql: 'SQL',
      ts: 'TypeScript',
      tsx: 'TypeScript (React)',
      yml: 'YAML',
    },
  }),
  directivesPlugin({ directiveDescriptors: [AdmonitionDirectiveDescriptor] }),
  diffSourcePlugin({ viewMode: 'rich-text' }),
  markdownShortcutPlugin(),
];

export const RichTextEditorSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      outputFormat: {
        type: 'string',
        enum: ['markdown', 'remarkup'],
        default: 'markdown',
      },
    },
  },
  returnValue: { type: 'string' },
};

export const useStyles = makeStyles(theme => ({
  editableContent: {
    backgroundColor: '#b7b7b70f',
  },
  label: {
    marginBottom: '8px',
  },
  previewContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #e0e0e0',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    maxHeight: '300px',
    overflow: 'auto',
  },
  formatToggle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

export const RichTextEditor = (
  props: FieldExtensionComponentProps<
    string,
    { outputFormat?: 'markdown' | 'remarkup' }
  >,
) => {
  const classes = useStyles();
  const { onChange, required, schema, formData, uiSchema } = props;

  const defaultFormat = uiSchema?.['ui:options']?.outputFormat || 'markdown';
  const [outputFormat, setOutputFormat] = useState<'markdown' | 'remarkup'>(
    defaultFormat,
  );
  const [markdownContent, setMarkdownContent] = useState(formData || '');
  const [remarkupPreview, setRemarkupPreview] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const updateContent = async () => {
      if (outputFormat === 'remarkup') {
        const converted = await convertMarkdownToPhabricatorRemarkup(
          markdownContent,
        );
        setRemarkupPreview(converted);

        onChange(converted);
      } else {
        onChange(markdownContent);
      }
    };

    updateContent();
  }, [markdownContent, outputFormat, onChange]);

  const handleEditorChange = (content: string) => {
    setMarkdownContent(content);
  };

  const handleFormatToggle = () => {
    setOutputFormat(prevFormat =>
      prevFormat === 'markdown' ? 'remarkup' : 'markdown',
    );
  };

  const { uploadFile } = useUploadFile();
  const { searchFile } = useSearchFile();

  const imageUploadHandler = async (image: File) : Promise<string> => {
    const imageBase64 = await convertToBase64(image);
    const dataBase64 = removeBase64Prefix(imageBase64);

    const resultUpload = await uploadFile({ dataBase64 });
    const filePHID = resultUpload.result;
    if(!filePHID) {
      return 'failed to upload file'
    }

    const resultSearch = await searchFile({ filePHID: filePHID ?? '' })
    if(resultSearch.error_code || !resultSearch.result.data) {
      return 'failed to get file detail'
    }
    const fileUrl = resultSearch.result.data[0]?.fields?.dataURI;

    return fileUrl ?? '';
  };

  return (
    <FormControl margin="normal" required={required} fullWidth>
      <FormLabel className={classes.label} required={required}>
        {schema.title}
      </FormLabel>

      <MDXEditor
        markdown={markdownContent}
        onChange={handleEditorChange}
        contentEditableClassName={classes.editableContent}
        plugins={allPlugins(imageUploadHandler)}
      />

      <Box className={classes.formatToggle}>
        <FormControlLabel
          control={
            <Switch
              checked={outputFormat === 'remarkup'}
              onChange={handleFormatToggle}
              color="primary"
            />
          }
          label={`Output in ${
            outputFormat === 'remarkup' ? 'Remarkup' : 'Markdown'
          } format`}
        />

        {outputFormat === 'remarkup' && (
          <FormControlLabel
            control={
              <Switch
                checked={showPreview}
                onChange={() => setShowPreview(!showPreview)}
                color="secondary"
              />
            }
            label="Show Remarkup Preview"
          />
        )}
      </Box>

      {showPreview && outputFormat === 'remarkup' && remarkupPreview && (
        <Box>
          <Typography variant="subtitle2">Remarkup Preview:</Typography>
          <Box className={classes.previewContainer}>{remarkupPreview}</Box>
        </Box>
      )}

      {schema.description && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
    </FormControl>
  );
};
